import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { probeService, websiteService } from '../services/api';
import { MainWPReport, MainWPSite, Website } from '../types';
import { ArrowPathIcon, PlusIcon, ExclamationCircleIcon, CheckCircleIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Type étendu pour le rapport MainWP avec les sites WordPress non gérés
interface ExtendedMainWPReport extends MainWPReport {
  wordpressSitesWithoutMainWP: WordPressSiteWithoutMainWP[];
  wordpressSitesWithoutMainWPCount: number;
}

// Type pour un site WordPress non géré par MainWP
interface WordPressSiteWithoutMainWP {
  id: string;
  name: string;
  url: string;
  wpVersion: string;
  lastScanned: string;
}

const MainWPReportPage: React.FC = () => {
  const [report, setReport] = useState<ExtendedMainWPReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'missing' | 'wordpress'>('missing');

  // Fonction pour charger le rapport
  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await probeService.getMainWPReport();
      setReport(data);
    } catch (err: any) {
      console.error('Erreur lors de la récupération du rapport MainWP:', err);
      setError(err.response?.data?.message || 'Impossible de charger le rapport. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rafraîchir le rapport
  const refreshReport = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const data = await probeService.getMainWPReport();
      setReport(data);
    } catch (err: any) {
      console.error('Erreur lors du rafraîchissement du rapport MainWP:', err);
      setError(err.response?.data?.message || 'Impossible de rafraîchir le rapport. Veuillez réessayer plus tard.');
    } finally {
      setRefreshing(false);
    }
  };

  // Fonction pour ajouter un site à la base de données
  const addSiteToDatabase = async (site: MainWPSite) => {
    try {
      // Créer un nouvel objet Website avec les informations de base
      const newWebsite: Omit<Website, 'id'> = {
        clientName: site.name,
        url: site.url,
        platform: 'WordPress',
        creationDate: new Date().toISOString().split('T')[0],
        anniversaryDate: new Date().toISOString().split('T')[0],
        hostingType: '',
        hostingProvider: '',
        ipAddress: '',
        serverConfig: `WordPress ${site.wpVersion}`,
        sslExpiration: '',
        contractType: '',
        amount: 0,
        lastBillingDate: '',
        nextBillingDate: '',
        paymentStatus: '',
        domain: new URL(site.url).hostname,
        domainExpiration: '',
        registrar: '',
        dnsConfig: {
          cloudflareProxy: false,
          cacheHeaders: false
        },
        emailType: 'smtp',
        emailNote: '',
        smtpServer: '',
        smtpEmail: '',
        smtpPassword: '',
        smtpPort: 0,
        encryption: '',
        postalHttpUrl: '',
        postalHttpApiKey: '',
        postalHttpSenderEmail: '',
        captchaType: '',
        bucketName: '',
        status: 'active',
        backupType: 'minio',
        backupNote: '',
        backupFrequency: '',
        maintenanceComments: '',
        cookiesManagement: '',
        primaryContact: '',
        contactEmail: '',
        contactPhone: '',
        comments: `Site importé depuis MainWP le ${new Date().toLocaleDateString('fr-FR')}`,
        // Champs pour la compatibilité
        emailAddresses: [],
        authType: ''
      };

      // Ajouter le site à la base de données
      await websiteService.create(newWebsite);
      
      // Rafraîchir le rapport
      await refreshReport();
    } catch (err: any) {
      console.error(`Erreur lors de l'ajout du site ${site.name}:`, err);
      setError(`Erreur lors de l'ajout du site ${site.name}: ${err.response?.data?.message || err.message}`);
    }
  };

  // Charger le rapport au chargement de la page
  useEffect(() => {
    loadReport();
  }, []);

  // Formater une date
  const formatDate = (dateString: string) => {
    try {
      if (!dateString || dateString === 'Inconnue') return 'Inconnue';
      const date = new Date(dateString);
      return format(date, 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch (err) {
      return dateString;
    }
  };

  // Vérifier si le rapport a des données à afficher
  const hasMissingMainWPSites = report && report.missingCount > 0;
  const hasWordPressSitesWithoutMainWP = report && report.wordpressSitesWithoutMainWPCount > 0;
  const hasDataToShow = hasMissingMainWPSites || hasWordPressSitesWithoutMainWP;

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Rapport MainWP
        </h1>
        <button
          onClick={refreshReport}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
        >
          <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : report ? (
        <>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Total sites MainWP</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{report.totalMainWPSites}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Total sites en base</h3>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{report.totalDbSites}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sites MainWP manquants</h3>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{report.missingCount}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">WP sans MainWP</h3>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{report.wordpressSitesWithoutMainWPCount}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Généré le</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{formatDate(report.generatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Onglets */}
          {hasDataToShow && (
            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('missing')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'missing'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
                  }`}
                >
                  Sites MainWP non référencés ({report.missingCount})
                </button>
                <button
                  onClick={() => setActiveTab('wordpress')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'wordpress'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
                  }`}
                >
                  WordPress sans MainWP ({report.wordpressSitesWithoutMainWPCount})
                </button>
              </nav>
            </div>
          )}

          {/* Contenu de l'onglet Sites MainWP non référencés */}
          {activeTab === 'missing' && (
            <>
              {report.missingCount > 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Nom du site
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            URL
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Dernière synchro
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Version WP
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {report.missingSites.map((site, index) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{site.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <a 
                                href={site.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {site.url}
                              </a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300">{formatDate(site.lastSync)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300">{site.wpVersion}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => addSiteToDatabase(site)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                Ajouter
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-900 border-l-4 border-green-500 text-green-700 dark:text-green-200 p-4 mb-6">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-2" />
                    <p>Tous les sites MainWP sont correctement référencés dans la base de données.</p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Contenu de l'onglet WordPress sans MainWP */}
          {activeTab === 'wordpress' && (
            <>
              {report.wordpressSitesWithoutMainWPCount > 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Nom du site
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            URL
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Version WP
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Dernière analyse
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {report.wordpressSitesWithoutMainWP.map((site, index) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{site.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <a 
                                href={site.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {site.url}
                              </a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300">{site.wpVersion}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300">{formatDate(site.lastScanned)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <Link
                                to={`/sites/${site.id}`}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Voir détails
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-900 border-l-4 border-green-500 text-green-700 dark:text-green-200 p-4 mb-6">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-2" />
                    <p>Tous les sites WordPress sont correctement gérés par MainWP.</p>
                  </div>
                </div>
              )}
            </>
          )}

          {!hasDataToShow && (
            <div className="bg-green-50 dark:bg-green-900 border-l-4 border-green-500 text-green-700 dark:text-green-200 p-4 mb-6">
              <div className="flex items-center">
                <CheckCircleIcon className="h-6 w-6 mr-2" />
                <p>Tout est en ordre : tous les sites MainWP sont référencés et tous les sites WordPress sont gérés par MainWP.</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-500 text-yellow-700 dark:text-yellow-200 p-4">
          <div className="flex items-center">
            <ExclamationCircleIcon className="h-6 w-6 mr-2" />
            <p>Aucun rapport disponible.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainWPReportPage; 